import {
    Alert,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Grid,
    useMediaQuery,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import React, { useEffect, useState } from 'react';
import { useGetAssetCalendarAvailabilityQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { useSelector } from 'react-redux';
import { selectCalendarError } from '../../../../app/Slices/CalendarError';
import FormCalendar, { CheckinDetails } from '../../../shared/FormCalendar';
import { FormProvider, useForm } from 'react-hook-form';
import { DateRange } from 'react-day-picker';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormValues } from '../../../types/experience';
import { format } from 'date-fns';
import { formatTime } from '../../../../shared/utils';
import UserRole from '../../../../shared/AuthRoles';

const Schema = z.object({
    date: z.object(
        {
            from: z.date(),
            to: z.date(),
        },
        { message: 'Selection required' },
    ),
    checkin: z.string().optional(),
    checkout: z.string().optional(),
});

function toValidDate(value?: string | Date): Date | undefined {
    if (!value) return undefined;
    return value instanceof Date ? value : new Date(value);
}

export default function CalendarModal(props: any) {
    const { onClose, open, onSubmit, data, reservation } = props;
    const dateSelectionError = useSelector(selectCalendarError);
    const isMobile = useMediaQuery('(max-width: 770px)');

    const loggedInUser = UserRole();

    const experience: FormValues = data;
    const reservationStartDate = toValidDate(reservation?.startDate);
    const defaultMonth = reservationStartDate ?? new Date();

    const methods = useForm({
        resolver: zodResolver(Schema),
        mode: 'onChange',
        shouldUnregister: true,
    });

    const { control, setValue, handleSubmit, watch } = methods;

    // const date = useWatch({ control, name: 'date' });
    const date = watch('date');

    const handleClose = () => {
        onClose();
    };

    const { data: availability } = useGetAssetCalendarAvailabilityQuery(
        {
            url: `/modules/${data?.id}/calendar`,
            bookingId: reservation?.id,
        },
        { skip: !open || !data?.id || !reservation?.id },
    );

    const [limitedDates, setLimitedDates] = useState<
        Record<string, CheckinDetails>
    >({});

    useEffect(() => {
        // map so that checkin/checkout data is available in O(n)
        if (availability?.limited) {
            const mappedLimitedDates = availability.limited.reduce(
                (acc: Record<string, CheckinDetails>, day: any) => {
                    acc[day.date] = {
                        checkinFrom: day.checkinFrom,
                        checkoutBefore: day.checkoutBefore,
                    };
                    return acc;
                },
                {},
            );

            setLimitedDates(mappedLimitedDates);
        }
    }, [availability, date]);

    const [alert, setAlert] = useState<string>('');

    const handleSelect = (date?: DateRange) => {
        setAlert('');

        if (experience?.minHireHours <= 24 && !date?.to) {
            setValue(
                'date',
                date
                    ? {
                          from: date?.from,
                          to: date?.from,
                      }
                    : null,
            );
        } else {
            setValue('date', date || null);
        }

        setValue('checkin', '');
        setValue('checkout', '');

        if (!date?.from || !date?.to) return;

        const checkin = format(date.from, 'yyyy-MM-dd');
        const checkout = format(date.to, 'yyyy-MM-dd');

        const checkinDetails = limitedDates[checkin];
        const checkoutDetails = limitedDates[checkout];

        if (checkinDetails?.checkinFrom) {
            setAlert(
                `Check-in available from ${formatTime(
                    checkinDetails.checkinFrom,
                )}`,
            );
            setValue('checkin', checkinDetails.checkinFrom);
        }

        if (
            (checkoutDetails?.checkoutBefore && !checkoutDetails.checkinFrom) ||
            (checkoutDetails?.checkoutBefore &&
                checkoutDetails.checkinFrom &&
                checkin !== checkout)
        ) {
            setAlert(
                `Checkout before ${formatTime(checkoutDetails.checkoutBefore)}`,
            );
            setValue('checkout', checkoutDetails.checkoutBefore);
        }
    };

    const endDate = new Date(
        new Date().setFullYear(
            new Date().getFullYear() +
                (loggedInUser?.role.includes('owner') ? 2 : 1),
        ),
    );

    const onNestedSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        if (event?.stopPropagation) event.stopPropagation();
        return handleSubmit(onSubmit)(event);
    };

    return (
        <Dialog open={open} onClose={handleClose}>
            <FormProvider {...methods}>
                <form onSubmit={onNestedSubmit}>
                    <DialogTitle>
                        Select new dates
                        <IconButton
                            onClick={handleClose}
                            sx={{ color: 'text.primary' }}
                            aria-label='close'
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <DialogContentText>
                            <Grid
                                sx={{
                                    overflowY: isMobile ? 'scroll' : 'hidden',
                                }}
                            >
                                {reservationStartDate ? (
                                    <FormCalendar
                                        name='date'
                                        control={control}
                                        availability={availability}
                                        experience={experience}
                                        selected={date}
                                        onSelect={handleSelect}
                                        defaultMonth={defaultMonth}
                                        startMonth={new Date()}
                                        endMonth={endDate}
                                        limitedDates={limitedDates}
                                    />
                                ) : null}
                                <Collapse in={alert !== ''}>
                                    <Alert severity='info' sx={{ mt: 1 }}>
                                        {alert}
                                    </Alert>
                                </Collapse>
                            </Grid>
                        </DialogContentText>
                    </DialogContent>{' '}
                    <DialogActions>
                        <Grid>
                            {' '}
                            <CustomButton
                                variant='contained'
                                color='neutral'
                                // autoFocus
                                onClick={handleClose}
                            >
                                Cancel
                            </CustomButton>
                        </Grid>
                        <Grid>
                            {' '}
                            <CustomButton
                                variant='contained'
                                color='primary'
                                type='submit'
                                disabled={dateSelectionError}
                            >
                                Save
                            </CustomButton>
                        </Grid>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
}
